/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit, ViewChild } from "@angular/core";
import {
  NgForm,
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
} from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { AuthService } from "app/core/auth/auth.service";
import { StaffService } from "app/services/staff.service";
import { FetchData } from "app/shared/generalMethods";
import {
  getAuth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";

@Component({
  selector: "app-staff-form",
  templateUrl: "./staff-form.component.html",
  styleUrls: ["./staff-form.component.scss"],
})
export class StaffFormComponent implements OnInit {
  @ViewChild("staffFormNgForm") staffFormNgForm: NgForm;
  staffForm: FormGroup;
  pageLoader: boolean = false;
  pageLoaderText: string;
  userRecord: any;
  dataSet: [];
  roleOptions = [{ key: "Staff", value: "Staff" }];
  adminEmailFirebase = "";
  adminId: string = "";
  permissionColumns: string[] = ["feature", "view", "create", "edit"];
  filteredPrograms: any[] = [];

  constructor(
    private _fetchData: FetchData,
    private _formBuilder: FormBuilder,
    private staffService: StaffService,
    private _snackBar: MatSnackBar,
    public router: Router,
    private authService: AuthService
  ) {
    this.userRecord = this._fetchData.data;
  }

  ngOnInit(): void {
    const user = JSON.parse(localStorage.getItem("user"));
    this.adminEmailFirebase = user ? user.email : "";
    this.adminId = localStorage.getItem("adminId") || "";
    this.filterPrograms();
    this.renderForm();
  }

  renderForm(): void {
    this.staffForm = this._formBuilder.group({
      name: ["", Validators.required],
      password: ["", [Validators.required, Validators.minLength(6)]],
      email: ["", [Validators.required, Validators.email]],
      address: [""],
      cellPhone: ["", [Validators.pattern("^[0-9]*$")]],
      role: [this.roleOptions[0]?.value, [Validators.required]],
      isActive: [true],
      administrator: [this.adminId, [Validators.required]],
      permissions: this._formBuilder.array([]),
    });
    this.initializePermissionControls();
  }

  initializePermissionControls(): void {
    const permissionArray = this.staffForm.get("permissions") as FormArray;
    permissionArray.clear();

    // Default permissions
    const defaultPermissions = [
      {
        feature: "Programs",
        view: false,
        create: false,
        edit: false,
        disableCreateEdit: true,
      },
      { feature: "Attendance", view: false, create: false, edit: false },
      {
        feature: "Dashboard",
        view: false,
        create: false,
        edit: false,
        disableCreateEdit: true,
      },
      {
        feature: "Reports",
        view: false,
        create: false,
        edit: false,
        disableCreateEdit: true,
      },
    ];

    defaultPermissions.forEach((permission) => {
      const group = this._formBuilder.group({
        feature: [permission.feature],
        view: [permission.view],
        create: [
          { value: permission.create, disabled: permission.disableCreateEdit },
        ],
        edit: [
          { value: permission.edit, disabled: permission.disableCreateEdit },
        ],
      });
      permissionArray.push(group);
    });
  }

  get permissionsArray(): FormArray {
    return this.staffForm.get("permissions") as FormArray;
  }

  get permissionsFormGroups() {
    return this.permissionsArray.controls as FormGroup[];
  }

  onInputNumeric(event: any): void {
    const input = event.target as HTMLInputElement;
    const pattern = /[0-9]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
    this.staffForm
      .get("cellPhone")
      ?.setValue(input.value, { emitEvent: false });
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;

    // Allow only numbers (0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  filterPrograms(): void {
    if (
      this._fetchData &&
      this._fetchData.data &&
      this._fetchData.data.lstPrograms &&
      this._fetchData.data.lstAdministratorData
    ) {
      const administratorId = this._fetchData.data.lstAdministratorData[0]?.Id;
      // Your filtering logic here
    }
  }

  onFormSubmit(): void {
    if (this.staffForm.invalid) {
      this.markFormGroupTouched(this.staffForm);
      this.openSnackBar("Please fill in all required fields correctly.");
      return;
    }

    this.pageLoader = true;
    this.pageLoaderText = "Creating staff...";
    const formData = this.prepareStaffData();
    const uID = localStorage.getItem("UID");

    if (!uID) {
      this.openSnackBar("User ID not found. Please login again.");
      this.pageLoader = false;
      return;
    }

    // Firebase Authentication: Create User
    const auth = getAuth();
    createUserWithEmailAndPassword(auth, formData.email, formData.password)
      .then((userCredential) => {
        const firebaseUser = userCredential.user;
        formData.firebaseUid = firebaseUser.uid;

        // Call the backend service to add the staff
        this.staffService.addStaff(formData, this.adminId).subscribe({
          next: (response: any) => {
            if (response.success) {
              this.openSnackBar(
                response.message || "Staff added successfully!"
              );

              // 📧 Send SendGrid verification email
              this.authService
                .sendVerificationEmailForStaff(
                  formData.name,
                  formData.email,
                  formData.password
                )
                .subscribe({
                  next: (res: any) => {
                    this.openSnackBar(
                      res.message || "Verification email sent!"
                    );
                  },
                  error: (err) => {
                    console.error(
                      "Error sending staff verification email:",
                      err
                    );
                    this.openSnackBar(
                      "Staff added, but failed to send verification email."
                    );
                  },
                });

              this.initializePermissionControls();
              this._fetchData.setLocalStorage(response.msg);
              this.router.navigate(["/profile-detail"]);
            } else {
              this.openSnackBar(
                response.error || "Failed to create staff member"
              );
            }
            this.pageLoader = false;
          },
          error: (err) => {
            this.openSnackBar(
              err.error?.message || "Failed to connect to server"
            );
            this.pageLoader = false;
          },
        });
      })
      .catch((error) => {
        this.openSnackBar(this.getFirebaseErrorMessage(error));
        this.pageLoader = false;
      });
  }

  private getFirebaseErrorMessage(error: any): string {
    switch (error.code) {
      case "auth/email-already-in-use":
        return "The email address is already in use by another account.";
      case "auth/invalid-email":
        return "The email address is not valid.";
      case "auth/operation-not-allowed":
        return "Email/password accounts are not enabled.";
      case "auth/weak-password":
        return "The password is too weak.";
      default:
        return "Failed to create user. Please try again.";
    }
  }
  generatePermissionJson(): void {
    const permissions = this.staffForm
      .get("permissions")
      .value.reduce((acc, permission) => {
        acc[permission.feature.toLowerCase()] = {
          view: permission.view,
          create: permission.create,
          edit: permission.edit,
        };
        return acc;
      }, {});
  }

  prepareStaffData(): any {
    const formValue = this.staffForm.getRawValue();
    const permissions = {};
    const organizations = this._fetchData.data?.lstOrganizations || [];
    const administrators = this._fetchData.data?.lstAdministratorData || [];

    // Get the first administrator's email
    const adminEmail = administrators[0]?.Email__c?.toLowerCase() || "";

    // Match organization using administrator's email instead of form email
    const matchedOrg = organizations.find(
      (org: any) => org.Email_Address__c?.toLowerCase() === adminEmail
    );

    const organizationId = matchedOrg ? matchedOrg.Id : null;
    formValue.permissions.forEach((permission) => {
      permissions[permission.feature.toLowerCase()] = {
        view: permission.view,
        create: permission.create || false,
        edit: permission.edit || false,
      };
    });

    return {
      administrator: formValue.administrator,
      name: formValue.name,
      email: formValue.email,
      address: formValue.address,
      isActive: formValue.isActive,
      role: formValue.role,
      cellPhone: formValue.cellPhone,
      password: formValue.password,
      firebaseUid: formValue.firebaseUid,
      permissions: permissions,
      organization: organizationId,
    };
  }

  openSnackBar(message: string): void {
    this._snackBar.open(message, "Close", {
      duration: 6000,
      horizontalPosition: "center",
      verticalPosition: "top",
    });
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      }
    });
  }
}
